import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle2,
  AlertTriangle,
  AlertOctagon,
  RefreshCw,
  DollarSign,
  Percent,
  Wallet,
  CreditCard,
  PieChart as PieIcon,
  Landmark,
  PiggyBank,
  Scale,
  BarChart3,
  LineChart as LineChartIcon,
} from 'lucide-react';
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
  Area,
  Bar,
  AreaChart,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  SIHAT: {
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    ring: 'ring-emerald-400',
    chip: 'bg-emerald-100 text-emerald-700',
    icon: CheckCircle2,
    dot: 'bg-emerald-500',
    gradient: 'from-emerald-50 to-white',
  },
  'PERLU PANTAU': {
    text: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    ring: 'ring-amber-400',
    chip: 'bg-amber-100 text-amber-700',
    icon: AlertTriangle,
    dot: 'bg-amber-500',
    gradient: 'from-amber-50 to-white',
  },
  KRITIKAL: {
    text: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    ring: 'ring-red-400',
    chip: 'bg-red-100 text-red-700',
    icon: AlertOctagon,
    dot: 'bg-red-500',
    gradient: 'from-red-50 to-white',
  },
};

const METRIC_ICONS = {
  revenue: DollarSign,
  cogs: CreditCard,
  gross_margin: Percent,
  opex: Wallet,
  net_profit: PieIcon,
  cash_flow: Activity,
  hutang: Landmark,
  cash_reserve: PiggyBank,
  current_ratio: Scale,
};

const formatValue = (val, unit) => {
  if (unit === 'RM') {
    return `RM ${Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (unit === '%') {
    return `${Number(val || 0).toFixed(1)}%`;
  }
  if (unit === 'x') {
    return val >= 999 ? '∞' : `${Number(val || 0).toFixed(2)}x`;
  }
  return String(val);
};

const TrendIcon = ({ trend, className = 'w-4 h-4' }) => {
  if (trend === 'up') return <TrendingUp className={`${className} text-emerald-600`} />;
  if (trend === 'down') return <TrendingDown className={`${className} text-red-600`} />;
  return <Minus className={`${className} text-gray-400`} />;
};

const StatusChip = ({ status }) => {
  const cfg = STATUS_COLORS[status] || STATUS_COLORS['PERLU PANTAU'];
  const Icon = cfg.icon;
  return (
    <span
      data-testid={`status-chip-${status.replace(/\s+/g, '-').toLowerCase()}`}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.chip}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {status}
    </span>
  );
};

const MetricCard = ({ metric }) => {
  const cfg = STATUS_COLORS[metric.status] || STATUS_COLORS['PERLU PANTAU'];
  const Icon = METRIC_ICONS[metric.key] || BarChart3;
  const pctDisplay =
    metric.pct_change === 0
      ? 'Stabil'
      : `${metric.pct_change > 0 ? '+' : ''}${metric.pct_change.toFixed(1)}%`;

  return (
    <div
      data-testid={`metric-card-${metric.key}`}
      className={`relative bg-gradient-to-br ${cfg.gradient} rounded-2xl p-5 border ${cfg.border} shadow-sm hover:shadow-lg transition-all duration-200`}
    >
      <div className={`absolute top-0 left-0 w-1 h-full rounded-l-2xl ${cfg.dot}`} />

      <div className="flex items-start justify-between mb-4">
        <div className={`p-2.5 rounded-xl ${cfg.bg}`}>
          <Icon className={`w-5 h-5 ${cfg.text}`} />
        </div>
        <StatusChip status={metric.status} />
      </div>

      <p className="text-gray-600 text-xs sm:text-sm font-medium leading-tight min-h-[2.5rem]">
        {metric.label}
      </p>

      <div className="mt-3 flex items-baseline gap-2 flex-wrap">
        <span
          data-testid={`metric-value-${metric.key}`}
          className="text-2xl sm:text-3xl font-bold text-gray-900"
        >
          {formatValue(metric.current, metric.unit)}
        </span>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-gray-500">
          <TrendIcon trend={metric.trend} />
          <span className="font-medium">{pctDisplay}</span>
          <span className="text-gray-400">vs bulan lepas</span>
        </div>
        <span className="text-gray-400">
          {formatValue(metric.previous, metric.unit)}
        </span>
      </div>

      {metric.note && (
        <p className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500 italic">
          {metric.note}
          {metric.months_of_runway !== undefined && metric.key === 'cash_reserve' && (
            <span className="ml-1 font-semibold text-gray-700">
              ({metric.months_of_runway} bln)
            </span>
          )}
        </p>
      )}
    </div>
  );
};

const OverallHealthHero = ({ overall, period }) => {
  const cfg = STATUS_COLORS[overall.status] || STATUS_COLORS['PERLU PANTAU'];
  const Icon = cfg.icon;

  // Circle gauge
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const score = Math.max(0, Math.min(100, overall.score || 0));
  const dashoffset = circumference - (score / 100) * circumference;
  const stroke =
    overall.status === 'SIHAT'
      ? '#10b981'
      : overall.status === 'KRITIKAL'
      ? '#ef4444'
      : '#f59e0b';

  return (
    <div
      data-testid="business-health-hero"
      className={`bg-white rounded-2xl p-4 sm:p-6 lg:p-8 border ${cfg.border} shadow-sm overflow-hidden`}
    >
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 sm:gap-5 min-w-0">
          <div className="relative flex-shrink-0">
            <svg width="100" height="100" viewBox="0 0 130 130" className="-rotate-90 sm:w-[130px] sm:h-[130px]">
              <circle
                cx="65"
                cy="65"
                r={radius}
                fill="none"
                stroke="#f1f5f9"
                strokeWidth="10"
              />
              <circle
                cx="65"
                cy="65"
                r={radius}
                fill="none"
                stroke={stroke}
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={dashoffset}
                style={{ transition: 'stroke-dashoffset 800ms ease-out' }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="overall-score">
                {score.toFixed(0)}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-gray-400">Skor</span>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-gray-400 font-semibold">
              Kesihatan Kewangan Keseluruhan
            </p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${cfg.text} flex-shrink-0`} />
              <h2
                data-testid="overall-status"
                className={`text-xl sm:text-2xl lg:text-3xl font-bold ${cfg.text} break-words`}
              >
                {overall.status}
              </h2>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 break-words">
              Tempoh: <span className="font-medium text-gray-700">{period.current_month}</span>
              <span className="mx-1.5 text-gray-300">•</span>
              dibanding <span className="font-medium text-gray-700">{period.previous_month}</span>
            </p>
            {period.fallback_used && (
              <p
                data-testid="period-fallback-note"
                className="mt-2 inline-flex items-center gap-1.5 text-[10px] sm:text-xs text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200 break-words"
              >
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                {period.fallback_note}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4 w-full lg:w-auto">
          <div className="bg-emerald-50 rounded-xl px-2 py-3 sm:px-4 text-center border border-emerald-100 min-w-0">
            <p className="text-xl sm:text-2xl font-bold text-emerald-700" data-testid="count-sihat">
              {overall.sihat_count}
            </p>
            <p className="text-[10px] sm:text-[11px] font-semibold text-emerald-700 uppercase tracking-wider mt-0.5">
              SIHAT
            </p>
          </div>
          <div className="bg-amber-50 rounded-xl px-2 py-3 sm:px-4 text-center border border-amber-100 min-w-0">
            <p className="text-xl sm:text-2xl font-bold text-amber-700" data-testid="count-pantau">
              {overall.pantau_count}
            </p>
            <p className="text-[10px] sm:text-[11px] font-semibold text-amber-700 uppercase tracking-wider mt-0.5">
              Pantau
            </p>
          </div>
          <div className="bg-red-50 rounded-xl px-2 py-3 sm:px-4 text-center border border-red-100 min-w-0">
            <p className="text-xl sm:text-2xl font-bold text-red-700" data-testid="count-kritikal">
              {overall.kritikal_count}
            </p>
            <p className="text-[10px] sm:text-[11px] font-semibold text-red-700 uppercase tracking-wider mt-0.5">
              Kritikal
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const TrendSection = ({ months = 6 }) => {
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/business-health/trend?months=${months}`,
          { withCredentials: true }
        );
        if (active) setTrend(res.data);
      } catch {
        if (active) setTrend(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [months]);

  if (loading) {
    return (
      <div
        data-testid="trend-loading"
        className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm flex items-center justify-center h-48"
      >
        <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!trend || !trend.series?.length) return null;

  const { series, summary, range } = trend;
  const hasRev = series.some(s => s.revenue > 0 || s.expenses > 0);
  const hasHutang = series.some(s => s.hutang !== null);
  // Only show current-ratio chart if at least one non-sentinel value exists
  const hasMeaningfulCR = series.some(s => s.current_ratio !== null && s.current_ratio < 999);

  return (
    <div
      data-testid="business-health-trend"
      className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-100 shadow-sm space-y-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <LineChartIcon className="w-5 h-5 text-red-600" />
            Trend {months} Bulan Terakhir
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            {range.from} → {range.to}
            <span className="mx-1.5 text-gray-300">•</span>
            {summary.months_with_snapshots} bulan ada snapshot lengkap
          </p>
        </div>
        <div className="flex gap-3 text-xs">
          <div className="bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-100">
            <span className="text-gray-500">Revenue:</span>
            <span className={`font-bold ml-1 ${summary.revenue_change_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {summary.revenue_change_pct >= 0 ? '+' : ''}{summary.revenue_change_pct.toFixed(1)}%
            </span>
          </div>
          <div className="bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100">
            <span className="text-gray-500">Cash Reserve:</span>
            <span className={`font-bold ml-1 ${summary.cash_reserve_change_pct >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
              {summary.cash_reserve_change_pct >= 0 ? '+' : ''}{summary.cash_reserve_change_pct.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      {/* Revenue vs Expenses */}
      {hasRev && (
        <div data-testid="trend-chart-rev-exp">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
            Revenue vs Expenses
          </p>
          <div style={{ width: '100%', height: 224 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => `RM ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="revenue" name="Revenue" fill="#10b981" radius={[6, 6, 0, 0]} />
                <Bar dataKey="expenses" name="Expenses" fill="#ef4444" radius={[6, 6, 0, 0]} />
                <Line type="monotone" dataKey="net_cashflow" name="Net Cashflow" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Cash Reserve cumulative */}
      <div data-testid="trend-chart-cash-reserve">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
          Simpanan Tunai (Cash Reserve) — Terkumpul
        </p>
        <div style={{ width: '100%', height: 192 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="cashGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => `RM ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
              <Area type="monotone" dataKey="cash_reserve" name="Cash Reserve" stroke="#3b82f6" strokeWidth={2} fill="url(#cashGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Hutang & Current Ratio (only if snapshots exist) */}
      {hasHutang ? (
        <div className={`grid grid-cols-1 ${hasMeaningfulCR ? 'md:grid-cols-2' : ''} gap-6`}>
          <div data-testid="trend-chart-hutang">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
              Hutang Tertunggak (AR/AP)
            </p>
            <div style={{ width: '100%', height: 192 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="hutangGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => v == null ? '—' : `RM ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                  <Area type="monotone" dataKey="hutang" name="Hutang" stroke="#f59e0b" strokeWidth={2} fill="url(#hutangGrad)" connectNulls={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {hasMeaningfulCR ? (
            <div data-testid="trend-chart-current-ratio">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                Nisbah Semasa (Current Ratio)
              </p>
              <div style={{ width: '100%', height: 192 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={series.map(s => ({ ...s, current_ratio_capped: s.current_ratio !== null && s.current_ratio >= 999 ? null : s.current_ratio }))} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="crGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v.toFixed(1)}x`} />
                    <Tooltip formatter={(v) => v == null ? '∞' : `${Number(v).toFixed(2)}x`} />
                    <Area type="monotone" dataKey="current_ratio_capped" name="Current Ratio" stroke="#8b5cf6" strokeWidth={2} fill="url(#crGrad)" connectNulls={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div data-testid="trend-current-ratio-infinity" className="bg-purple-50 border border-purple-100 rounded-xl p-4 flex flex-col items-center justify-center">
              <Scale className="w-8 h-8 text-purple-400 mb-2" />
              <p className="text-sm font-semibold text-purple-700">Nisbah Semasa: ∞</p>
              <p className="text-xs text-purple-600 mt-1 text-center">Tiada hutang tertunggak — kedudukan tunai sangat kukuh</p>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-center">
          <p className="text-sm text-gray-500">
            <AlertTriangle className="w-4 h-4 inline text-amber-500 mr-1.5 align-text-bottom" />
            Snapshot Hutang & Current Ratio belum terkumpul — trend akan muncul secara automatik
            setiap kali dashboard dilayari bulan baru. Ia akan lengkap selepas {months} bulan penggunaan.
          </p>
        </div>
      )}
    </div>
  );
};

const BusinessHealthPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/business-health`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Ralat memuatkan data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading && !data) {
    return (
      <div
        data-testid="business-health-loading"
        className="flex items-center justify-center h-64"
      >
        <div className="flex items-center gap-3 text-gray-500">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Memuat naik Quick Snapshot…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="business-health-error"
        className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700"
      >
        <p className="font-semibold">Gagal memuatkan data kesihatan perniagaan</p>
        <p className="text-sm mt-1">{error}</p>
        <button
          onClick={fetchData}
          data-testid="business-health-retry"
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
        >
          Cuba Semula
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div data-testid="business-health-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-7 h-7 text-red-600" />
            Quick Snapshot
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Ringkasan kesihatan kewangan syarikat untuk pemilik & akauntan
          </p>
        </div>
        <button
          onClick={fetchData}
          data-testid="business-health-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700 shadow-sm self-start"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Muat Semula
        </button>
      </div>

      <OverallHealthHero overall={data.overall} period={data.period} />

      {/* Metrics grid */}
      <div
        data-testid="business-health-metrics-grid"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        {data.metrics.map((m) => (
          <MetricCard key={m.key} metric={m} />
        ))}
      </div>

      {/* 6-month trend */}
      <TrendSection months={6} />

      {/* Thresholds legend */}
      <div
        data-testid="business-health-thresholds"
        className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm"
      >
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-gray-500" />
          Panduan Klasifikasi Status
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs text-gray-600">
          {Object.entries(data.thresholds || {}).map(([k, v]) => (
            <div key={k} className="flex gap-2 items-start">
              <span className="text-gray-400 shrink-0">•</span>
              <span>
                <span className="font-semibold text-gray-800 capitalize">
                  {k.replace(/_/g, ' ')}:
                </span>{' '}
                {v}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BusinessHealthPage;
